'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavbar } from '@src/v3/contexts/NavbarContext';
import Image from 'next/image';
import { getUserType, isLoggedIn } from '@src/utils/localStorage';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  APP_ROUTE,
  NAVBAR_ROUTES,
  RENTER_ROUTES,
  AGENT_ROUTES,
} from '@src/v3/constants/routes';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import Loader from '../loader';
import useLoader from '@src/hooks/useLoader';
import Link from 'next/link';
import LoginModal from '@src/v3/components/loginModal';
import RegisterModal from '../registerModal';
import { resetPostHogUser } from '@src/v3/utils/analytics';
import { useUnreadMessages } from '@src/v3/contexts/unreadMessagesContext';
import clsx from 'clsx';
import Button from '@src/v3/components/coreUI/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/v3/components/coreUI/dropdownMenu';
import Icon from '@src/v3/components/coreUI/icon';
import { logoutUser } from '@src/v3/services/auth';
import { Separator } from '../coreUI/separator';
import {
  clearNylasModalDismissalState,
  clearApplicationSessionFlags,
} from '@src/v3/utils/localStorage';
import useProfile from '@src/v3/hooks/useProfile';
import { SUBSCRIPTION_STATUS_ENUM } from '@src/v3/constants/configs';
import userAvatar from '../../../../public/user-avatar.svg';
import useOrganization from '@src/v3/hooks/useOrganization';
import ProfileShareModal from '../property/publicProfile/profileShareModal';

const Header = ({ isSticky = true }: { isSticky?: boolean }) => {
  const { isNavbarVisible } = useNavbar();
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isLoading, showLoader, hideLoader } = useLoader();
  const { profile, isUserLoggedIn, setIsUserLoggedIn } = useProfile();

  // State
  const [menus, setMenus] = useState(NAVBAR_ROUTES);
  const { hasUnreadCount } = useUnreadMessages();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState(userAvatar);
  const [
    isVisibleSubscriptionFailedBanner,
    setIsVisibleSubscriptionFailedBanner,
  ] = useState<boolean>(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] =
    useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(false);
  const [hasHandledInviteLoginParam, setHasHandledInviteLoginParam] =
    useState<boolean>(false);

  // Organization membership - only JOINED members can access admin panel
  const { hasMembership, membership, organization } = useOrganization();
  const canAccessAdminPanel =
    hasMembership &&
    membership?.status === 'JOINED' &&
    membership?.can_access_admin_panel &&
    !isMobileViewport;
  // Deal Manager is org-wide: any JOINED member when the org is entitled.
  const canAccessDealManager =
    hasMembership &&
    membership?.status === 'JOINED' &&
    !!organization?.deal_manager_enabled &&
    !isMobileViewport;

  useEffect(() => {
    const checkMobile = () => setIsMobileViewport(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Support org invite email links for logged-out users:
  // open login modal on agent home so auth can complete and pending invite modal appears after login.
  useEffect(() => {
    if (hasHandledInviteLoginParam) return;
    const shouldPromptLogin =
      searchParams?.get('org_invite') === '1' &&
      searchParams?.get('auth') === 'login';
    if (shouldPromptLogin && !isLoggedIn()) {
      setIsLoginModalOpen(true);
      setHasHandledInviteLoginParam(true);
    }
  }, [searchParams, hasHandledInviteLoginParam]);

  // Helper function to get the correct home URL
  const getHomeUrl = () => {
    if (isLoggedIn() && getUserType() === ZEME_USERS_ROLE.RENTER) {
      return APP_ROUTE.HOME;
    }
    if (
      isLoggedIn() &&
      (getUserType() === ZEME_USERS_ROLE.AGENT ||
        getUserType() === ZEME_USERS_ROLE.LANDLORD)
    ) {
      return APP_ROUTE.AGENT_HOME;
    }
    return APP_ROUTE.HOME;
  };
  // Get navbar menus based on user type
  const getMenus = useCallback(() => {
    if (isLoggedIn() && getUserType() === ZEME_USERS_ROLE.RENTER) {
      // Filter routes that should be visible in header
      const headerRoutes = RENTER_ROUTES.filter(
        (route) => route.isVisibleInHeader
      );
      return setMenus(headerRoutes);
    }

    if (
      isLoggedIn() &&
      (getUserType() === ZEME_USERS_ROLE.AGENT ||
        getUserType() === ZEME_USERS_ROLE.LANDLORD)
    ) {
      // Filter agent routes based on profile features
      const filteredAgentRoutes = AGENT_ROUTES.map((route) => {
        if (route.label === 'Just Credit') {
          return {
            ...route,
            isVisible: profile?.agentInformation?.enableOneoffCredit || false,
          };
        }
        if (route.label === 'General Application') {
          return {
            ...route,
            isVisible:
              profile?.agentInformation?.enableGeneralApplications || false,
          };
        }
        return route;
      });
      return setMenus(filteredAgentRoutes);
    }

    setMenus(NAVBAR_ROUTES);
    return false;
  }, [profile]);

  useEffect(() => {
    if (profile) {
      setIsVisibleSubscriptionFailedBanner(
        (getUserType() === ZEME_USERS_ROLE.AGENT ||
          getUserType() === ZEME_USERS_ROLE.LANDLORD) &&
          (profile?.generalInformation?.currentSubscription?.status ===
            SUBSCRIPTION_STATUS_ENUM.FAILED ||
            profile?.generalInformation?.currentSubscription?.status ===
              SUBSCRIPTION_STATUS_ENUM.CANCELLED)
      );

      let image = userAvatar;

      if (profile?.landlordInformation?.profilePictureThumbnail)
        image = profile?.landlordInformation?.profilePictureThumbnail;

      if (profile?.agentInformation?.profilePictureThumbnail)
        image = profile?.agentInformation?.profilePictureThumbnail;

      setProfileImage(image);
    } else {
      setIsVisibleSubscriptionFailedBanner(false);
    }
  }, [profile]);

  /**
   * Handles the logout functionality.
   *
   * @return {Promise<void>} Returns a promise that resolves when the logout process is complete.
   */
  const handleLogout = async () => {
    try {
      showLoader();
      await logoutUser();
      // showSuccessMsg(AUTH_MSG.logoutSuccess);
      localStorage.clear();
      clearApplicationSessionFlags();
      clearNylasModalDismissalState(); // Clear modal dismissal state on logout
      resetPostHogUser();
      setIsUserLoggedIn?.(false);
      push(APP_ROUTE.EXPLORE);
    } catch (error) {
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    getMenus();
  }, [getMenus, isUserLoggedIn]);

  if (!isNavbarVisible) {
    return null;
  }

  return (
    <>
      <header
        className={clsx(
          isSticky ? 'fixed top-0' : '',
          'hidden sm:block w-full z-50'
        )}
        id="zeme-header"
      >
        {isVisibleSubscriptionFailedBanner &&
        pathname != APP_ROUTE.SUBSCRIPTION ? (
          <div className="flex justify-center items-center w-full bg-error text-xs p-3 text-white">
            <Icon iconName="error" iconProps={{ className: 'h-6 w-6 mr-2' }} />
            <span>
              Payment failed. You’ll lose your listings if we don’t have a
              working payment method for your account, so please{' '}
              <span
                className="underline cursor-pointer"
                // onClick={() => setIsOpenPayment(true)}
              >
                {' '}
                update your payment details.
              </span>
            </span>
          </div>
        ) : null}
        <div className="bg-white border-b border-gray-200 px-10 py-5 relative">
          <nav className="relative z-10 h-full">
            {/* Agent/Landlord Header - Center aligned menus */}
            {isUserLoggedIn &&
            (getUserType() === ZEME_USERS_ROLE.AGENT ||
              getUserType() === ZEME_USERS_ROLE.LANDLORD) ? (
              <div className="flex items-center justify-between mx-auto h-full">
                {/* Logo */}
                <div className="flex-shrink-0">
                  <Link
                    href="/"
                    onClick={(e) => {
                      e.preventDefault();
                      push(getHomeUrl());
                    }}
                  >
                    <Image
                      src="/zeme-colored-svg.svg"
                      alt="Zeme Logo"
                      width={75}
                      height={16}
                    />
                  </Link>
                </div>

                {/* Center Menus */}
                <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-x-3">
                  {menus.map(
                    (
                      {
                        label = '',
                        value = '',
                        isVisible = false,
                        isVisibleBadge = false,
                      },
                      index
                    ) => {
                      // Check if current pathname matches the route value
                      // Also check for v3 version of dashboard route (e.g., /v3/agent/dashboard or /v3/landlord/dashboard)
                      const isActive =
                        pathname === value ||
                        (value === APP_ROUTE.AGENT_HOME &&
                          /^\/v3\/(agent|landlord)\/dashboard/.test(pathname));

                      return isVisible ? (
                        <Link
                          href={value}
                          key={index}
                          type="button"
                          onClick={() => push(value)}
                          className={clsx(
                            'flex items-center h-12 px-5 py-2 rounded-xl text-base font-medium transition-colors leading-none',
                            isActive
                              ? 'text-focus'
                              : 'text-black-dark hover:text-primary-dark'
                          )}
                        >
                          <span>{label}</span>
                          <div className="relative">
                            {isVisibleBadge && hasUnreadCount && (
                              <div className="absolute -top-3 -right-2.5 h-3 w-3 rounded-full bg-focus"></div>
                            )}
                          </div>
                        </Link>
                      ) : null;
                    }
                  )}
                </div>

                {/* Right Section - List a home button and Profile */}
                <div className="flex items-center gap-x-3 ml-auto">
                  {pathname !== APP_ROUTE.AGENT_ADD_NEW_PROPERTY && (
                    <Button
                      variant="gray"
                      size="lg"
                      className="flex items-center gap-x-2 text-base font-medium text-black-dark hover:bg-transparent rounded-full px-5"
                      onClick={() => push(APP_ROUTE.AGENT_ADD_NEW_PROPERTY)}
                    >
                      <Icon
                        iconName="plus"
                        iconProps={{ className: 'w-5 h-5' }}
                      />
                      List a home
                    </Button>
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full h-12 w-12"
                      >
                        <Image
                          src={profileImage}
                          width={48}
                          height={48}
                          alt="Rounded avatar"
                          className="rounded-full"
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="flex flex-col gap-y-3 min-w-[256px] max-w-[50vw] px-4 pt-3 pb-6 border-0 shadow-md rounded-xl"
                    >
                      <div className="flex items-center gap-x-2 py-4">
                        <Image
                          src={
                            profile?.agentInformation
                              ?.profilePictureThumbnail ||
                            profile?.landlordInformation
                              ?.profilePictureThumbnail ||
                            userAvatar
                          }
                          width={32}
                          height={32}
                          alt="Rounded avatar"
                          className="rounded-full flex-shrink-0"
                        />
                        <div className="flex flex-col min-w-0 flex-1 text-gray-1000">
                          <span className="text-base font-medium leading-[12px]">
                            Hello
                          </span>
                          <span className="text-sm capitalize truncate block max-w-full">
                            {profile?.generalInformation?.firstName}{' '}
                            {profile?.generalInformation?.lastName}
                          </span>
                        </div>
                      </div>
                      {canAccessAdminPanel && (
                        <>
                          <Separator className="bg-gray-200" />
                          <DropdownMenuItem
                            onClick={() => push(APP_ROUTE.ADMIN_PANEL)}
                            className="cursor-pointer flex items-center gap-x-2 p-0 text-sm font-medium tracking-[-0.14px] text-gray-1000 !hover:bg-transparent focus:bg-transparent"
                          >
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100">
                              <Icon
                                iconName="usersThree"
                                iconProps={{
                                  className: 'h-3.5 w-3.5 text-black-700',
                                }}
                              />
                            </span>
                            Admin Panel
                          </DropdownMenuItem>
                          <Separator className="bg-gray-200" />
                        </>
                      )}
                      {canAccessDealManager && (
                        <>
                          {!canAccessAdminPanel && (
                            <Separator className="bg-gray-200" />
                          )}
                          <DropdownMenuItem
                            onClick={() => push(APP_ROUTE.DEAL_MANAGER)}
                            className="cursor-pointer flex items-center gap-x-2 p-0 text-sm font-medium tracking-[-0.14px] text-gray-1000 !hover:bg-transparent focus:bg-transparent"
                          >
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100">
                              <Icon
                                iconName="receipt"
                                iconProps={{
                                  className: 'h-3.5 w-3.5 text-black-700',
                                }}
                              />
                            </span>
                            Deal Manager
                          </DropdownMenuItem>
                          <Separator className="bg-gray-200" />
                        </>
                      )}
                      <DropdownMenuItem
                        onClick={() => push(APP_ROUTE.SETTINGS)}
                        className="cursor-pointer flex gap-x-2 p-0 text-sm font-medium tracking-[-0.14px] text-[#343330] !hover:bg-transparent focus:bg-transparent"
                      >
                        <Icon
                          iconName="settings"
                          iconProps={{
                            className: 'h-[18px] w-[18px] text-[#343330]',
                          }}
                        />
                        Settings
                      </DropdownMenuItem>
                      {getUserType() === ZEME_USERS_ROLE.AGENT ||
                      getUserType() === ZEME_USERS_ROLE.LANDLORD ? (
                        <DropdownMenuItem
                          onClick={() => setIsShareModalOpen(true)}
                          className="cursor-pointer flex gap-x-2 p-0 text-sm font-medium tracking-[-0.14px] text-[#343330] !hover:bg-transparent focus:bg-transparent"
                        >
                          <Icon
                            iconName="shareIcon"
                            iconProps={{
                              className: 'h-[18px] w-[18px] text-[#343330]',
                            }}
                          />
                          Share Profile
                        </DropdownMenuItem>
                      ) : null}
                      <Separator className="bg-gray-200" />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer flex gap-x-2 p-0 text-sm font-medium tracking-[-0.14px] text-red-700 focus:text-red-700 focus:bg-transparent"
                      >
                        <Icon
                          iconName="logout"
                          iconProps={{ className: 'h-4 w-4 text-red-800' }}
                        />
                        Log Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ) : (
              /* Renter/Guest Header - Default layout */
              <div className="flex flex-wrap justify-between items-center mx-auto h-full">
                <div className="flex gap-6 cursor-pointer">
                  <Link
                    href="/"
                    onClick={(e) => {
                      e.preventDefault();
                      push(getHomeUrl());
                    }}
                  >
                    <Image
                      src="/zeme-colored-svg.svg"
                      alt="Zeme Logo"
                      width={75}
                      height={16}
                    />
                  </Link>
                </div>
                {/*Header right section button */}
                <div className="flex items-center gap-x-3">
                  <div className="hidden xs:flex items-center gap-x-3">
                    {menus.map(
                      (
                        {
                          label = '',
                          value = '',
                          isVisible = false,
                          isVisibleBadge = false,
                        },
                        index
                      ) => {
                        return isVisible ? (
                          <Link
                            href={value}
                            key={index}
                            type="button"
                            onClick={() => push(value)}
                            className={clsx(
                              'flex items-center h-12 px-5 py-2 rounded-xl text-base font-medium transition-colors leading-none',
                              pathname === value
                                ? 'text-primary-light'
                                : 'text-black-dark hover:text-primary-dark'
                            )}
                          >
                            <span>{label}</span>
                            <div className="relative">
                              {isVisibleBadge && hasUnreadCount && (
                                <div className="absolute -top-3 -right-2.5 h-3 w-3 rounded-full bg-focus"></div>
                              )}
                            </div>
                          </Link>
                        ) : null;
                      }
                    )}
                  </div>

                  {isUserLoggedIn ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-full h-12 w-12"
                        >
                          <Image
                            src={profileImage}
                            width={48}
                            height={48}
                            alt="Rounded avatar"
                            className="rounded-full"
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="flex flex-col gap-y-3 min-w-[256px] max-w-[50vw] px-4 pt-3 pb-6 border-0 shadow-md rounded-xl"
                      >
                        <div className="flex items-center gap-x-2 py-4">
                          <div className="w-8 h-8 rounded-full bg-gray-1000 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                            {profile?.generalInformation?.firstName
                              ?.slice(0, 1)
                              .toUpperCase()}
                            {profile?.generalInformation?.lastName
                              ?.slice(0, 1)
                              .toUpperCase()}
                          </div>
                          <div className="flex flex-col min-w-0 flex-1 text-gray-1000">
                            <span className="text-base font-medium leading-[20px]">
                              Hello
                            </span>
                            <span className="text-sm capitalize truncate block leading-[16px] max-w-full">
                              {profile?.generalInformation?.firstName}{' '}
                              {profile?.generalInformation?.lastName}
                            </span>
                          </div>
                        </div>
                        <DropdownMenuItem
                          onClick={() => push(APP_ROUTE.SETTINGS)}
                          className="cursor-pointer flex gap-x-2 p-0 text-sm font-medium tracking-[-0.14px] text-gray-1000 !hover:bg-transparent focus:bg-transparent"
                        >
                          <Icon
                            iconName="settings"
                            iconProps={{ className: 'h-4 w-4' }}
                          />
                          Settings
                        </DropdownMenuItem>
                        <Separator className="bg-gray-600" />
                        <DropdownMenuItem
                          onClick={handleLogout}
                          className="cursor-pointer flex gap-x-2 p-0 text-sm font-medium tracking-[-0.14px] text-red-700 focus:text-red-700 focus:bg-transparent"
                        >
                          <Icon
                            iconName="logout"
                            iconProps={{ className: 'h-4 w-4 text-red-800' }}
                          />
                          Log Out
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <div className="flex items-center gap-x-3">
                      <Button
                        variant="outline"
                        size="md"
                        className="rounded-full text-gray-1000 border-gray-1000 hover:border-primary-dark hover:text-primary-dark hover:bg-transparent w-20 h-11"
                        onClick={() => setIsLoginModalOpen(true)}
                      >
                        Login
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        className="rounded-full w-24 h-11"
                        onClick={() => setIsRegisterModalOpen(true)}
                      >
                        Sign Up
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </nav>

          <Image
            src="/header-bg.svg"
            width={145}
            height={80}
            alt="header background"
            className="absolute top-0 right-0 w-auto h-[80px] max-h-full"
          />
        </div>
      </header>
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        overlayClassName=""
        onOpenRegisterModal={() => {
          setIsLoginModalOpen(false);
          setIsRegisterModalOpen(true);
        }}
      />
      <RegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        overlayClassName=""
        onOpenLoginModal={() => {
          setIsRegisterModalOpen(false);
          setIsLoginModalOpen(true);
        }}
      />
      {isShareModalOpen && profile ? (
        <ProfileShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          profileId={
            profile?.agentInformation?.profileUrlIdentifier ||
            profile?.landlordInformation?.profileUrlIdentifier ||
            ''
          }
          profileImage={
            profile?.agentInformation?.profilePictureThumbnail ||
            profile?.landlordInformation?.profilePictureThumbnail
          }
          profileName={`${profile?.generalInformation?.firstName || ''} ${
            profile?.generalInformation?.lastName || ''
          }`.trim()}
          companyName={
            profile?.agentInformation?.companyName ||
            profile?.landlordInformation?.companyName
          }
        />
      ) : null}
      {isLoading ? <Loader isVisible={isLoading} /> : null}
    </>
  );
};

export default Header;
